import axios from 'axios';

import { Challenge } from '@/types/Challenge';
import { Judge } from '@/types/Judge';
import { Post, PostSchema } from '@/types/Post';
import { Response } from '@/types/Response';

import { setupInterceptors } from './axiosInterceptor';

// TODO parameterize 'dev' as an environment variable
const USER_SERVICE_URL =
    process.env.VUE_APP_USERSERVICE_BASE_URL || 'https://dev-userservice-us-east-1.duelly.io';
const NOTIFY_URL =
    process.env.VUE_APP_NOTIFYSERVICE_BASE_URL ||
    'https://dev-notificationservice-us-east-1.duelly.io';
const CHALLENGE_URL =
    process.env.VUE_APP_CHALLENGESERVICE_BASE_URL ||
    'https://dev-challangeservice-us-east-1.duelly.io';
const ADMIN_SERVICE_URL =
    process.env.VUE_APP_ADMIN_SERVICE_BASE_URL || 'https://dev-admin-service-us-east-1.duelly.io';
const LOG_URL =
    process.env.VUE_APP_LOGSERVICE_BASE_URL || 'https://dev-logservice-us-east-1.duelly.io';

const userServiceClient = axios.create({
    baseURL: USER_SERVICE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

const notificationServiceClient = axios.create({
    baseURL: NOTIFY_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

const challengeServiceClient = axios.create({
    baseURL: CHALLENGE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

const adminServiceClient = axios.create({
    baseURL: ADMIN_SERVICE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

const logServiceClient = axios.create({
    baseURL: LOG_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

export {
    adminServiceClient,
    challengeServiceClient,
    logServiceClient,
    notificationServiceClient,
    userServiceClient,
};

const clients = [
    userServiceClient,
    notificationServiceClient,
    challengeServiceClient,
    logServiceClient,
    adminServiceClient,
];

clients.forEach((client) => {
    setupInterceptors(client);
});

export default {
    log(
        message: string,
        level: string,
        user_id: string,
        user_email: string,
        user_name: string,
        user_role: string
    ) {
        return logServiceClient.post('/front-end-logs', {
            message,
            level,
            user_id,
            user_email,
            user_name,
            user_role,
        });
    },

    like(parent_type: string, parent_id: string) {
        return challengeServiceClient.post('/challenge/like', {
            parent_type,
            parent_id,
        });
    },
    unlike(parent_type: string, parent_id: string) {
        return challengeServiceClient.post('/challenge/unlike', {
            parent_type,
            parent_id,
        });
    },
    deleteContent(content_type: 'challenge' | 'post' | 'comment', content_id: string) {
        return challengeServiceClient.delete(`/challenge/content/${content_type}/${content_id}`);
    },

    createChallenge(type: string, videoType: string, parent_id: string, numParts = 1) {
        return challengeServiceClient.post(`/challenge/create`, {
            type,
            numParts,
            videoType,
            parent_id,
        });
    },

    createResponse(
        type: string,
        videoType: string,
        parent_id: string,
        challengeUserId: string,
        numParts = 1,
        responseId?: string
    ) {
        console.log('createResponse: ', type, videoType, parent_id, challengeUserId, numParts);
        return challengeServiceClient.post(`/challenge/create-response`, {
            type,
            numParts,
            videoType,
            challenge_user_id: challengeUserId,
            challenge_id: parent_id,
            response_id: responseId,
        });
    },

    createPost(videoType: string, parent_id: string, type: string, numParts = 1, post_id?: string) {
        //remove POST# from post_id if it exists
        if (post_id && post_id.startsWith('POST#')) {
            post_id = post_id.replace('POST#', '');
        }
        return challengeServiceClient.post(`/challenge/create-post`, {
            videoType,
            parent_id, // I think this can be removed
            type, // I think this can be removed
            numParts,
            post_id, // This is optional, used for updating an existing post
        });
    },

    async savePost(postData: Record<string, any>): Promise<Post> {
        const { write_tags, ...postWithoutWriteTags } = postData;

        // sanitize everything except write_tags
        const payload = PostSchema.strip().parse(postWithoutWriteTags);

        // only include write_tags if present
        const body = write_tags !== undefined ? { ...payload, write_tags } : payload;

        const response = await challengeServiceClient.post('/challenge/save-post', body);
        return response.data;
    },

    async getRecentPosts(
        limit = 10,
        last_evaluated_key: any = null
    ): Promise<{
        posts: Post[];
        user_profiles: Record<string, any>;
        user_likes: string[];
        lastEvaluatedKey: any;
    }> {
        const response = await challengeServiceClient.get('/challenge/get-recent-posts', {
            params: {
                limit,
                last_evaluated_key: last_evaluated_key
                    ? JSON.stringify(last_evaluated_key)
                    : undefined,
            },
        });
        console.log('getRecentPosts response:', response);

        const posts: Post[] = response.data.posts;
        const lastEvaluatedKey = response.data.last_evaluated_key || null;
        const user_profiles = response.data.user_profiles || {};
        const user_likes = response.data.user_likes || [];

        return { posts, user_profiles, user_likes, lastEvaluatedKey };
    },
    async getPost(post_id: string): Promise<{ post: Post; user_likes: string[] }> {
        if (!post_id) {
            throw new Error('Post ID is required');
        } else if (post_id.startsWith('POST#PLACEHOLDER')) {
            throw new Error('Cannot fetch placeholder post');
        }
        console.log('islIked getPost called with post_id:', post_id);
        const response = await challengeServiceClient.get('/challenge/get-post', {
            params: { post_id },
        });
        console.log('getPost response:', response.data);

        if (!response?.data?.post) {
            throw new Error('Post not found');
        }

        return {
            post: response.data.post,
            user_likes: response.data.user_likes || [],
        };
    },

    createPrizeVideo(
        videoType: string,
        parent_id: string,
        prize_type = 'competitor-prize',
        numParts = 1
    ) {
        return challengeServiceClient.post(`/challenge/create-prize-video`, {
            numParts,
            videoType,
            challenge_id: parent_id,
            prize_type,
        });
    },

    getResponseById(response_id: string) {
        return challengeServiceClient.get(`/challenge/get-response`, {
            params: {
                response_id,
            },
        });
    },

    fetchJudgesByUserId(access_token: string, user_id: string, parent_ids: string[]) {
        return challengeServiceClient.get(`/challenge/fetch-judges-by-user-id`, {
            params: {
                user_id,
                parent_ids,
            },
            headers: {
                Authorization: `Bearer ${access_token}`,
                'Content-Type': 'application/json',
            },
        });
    },

    getJudgedResponse(access_token: string, user_id: string, response_id: string[]) {
        return challengeServiceClient.get(`/challenge/get-judged-response`, {
            params: {
                user_id,
                response_id,
            },
            headers: {
                Authorization: `Bearer ${access_token}`,
                'Content-Type': 'application/json',
            },
        });
    },

    async getUserChallengeResponsesCount(challenge_id: string): Promise<number> {
        const resp = await challengeServiceClient.get('/challenge/get-challenge-responses-count', {
            params: {
                challenge_id: challenge_id,
            },
        });
        return resp.data.count ? resp.data.count : 0;
    },

    async getJudgeCount(challenge_id: string): Promise<number> {
        const resp = await challengeServiceClient.get('/challenge/get-judge-count', {
            params: {
                challenge_id: challenge_id,
            },
        });
        return resp.data.count ? resp.data.count : 0;
    },

    async getUserChallenges(
        user_id: string,
        limit = 10,
        last_evaluated_key: any = null
    ): Promise<{ challenges: Challenge[]; lastEvaluatedKey: any }> {
        const response = await challengeServiceClient.get('/challenge/user-challenges', {
            params: {
                user_id,
                limit,
                last_evaluated_key: last_evaluated_key ? JSON.stringify(last_evaluated_key) : null,
            },
        });
        if (!response.data.challenges || response.data.challenges.length === 0) {
            return { challenges: [], lastEvaluatedKey: null };
        }
        return {
            challenges: response.data.challenges,
            lastEvaluatedKey: response.data.last_evaluated_key || null,
        };
    },

    async getUserJudgedChallenges(
        accessToken: string,
        user_id: string,
        limit = 10,
        last_evaluated_key: any = null
    ): Promise<{ challenges: Challenge[]; judges: Judge[]; lastEvaluatedKey: any }> {
        const response = await challengeServiceClient.get('/challenge/user-judged-challenges', {
            params: {
                user_id,
                limit,
                last_evaluated_key: last_evaluated_key ? JSON.stringify(last_evaluated_key) : null,
            },
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
        });
        if (!response.data.challenges || response.data.challenges.length === 0) {
            return { challenges: [], judges: [], lastEvaluatedKey: null };
        }
        return {
            judges: response.data.judges,
            challenges: response.data.challenges,
            lastEvaluatedKey: response.data.last_evaluated_key || null,
        };
    },

    async getUserResponses(
        user_id: string,
        limit = 10,
        last_evaluated_key: any = null
    ): Promise<{ responses: Response[]; lastEvaluatedKey: any }> {
        const response = await challengeServiceClient.get('/challenge/user-responses', {
            params: {
                user_id,
                limit,
                last_evaluated_key: last_evaluated_key ? JSON.stringify(last_evaluated_key) : null,
            },
        });
        if (!response.data.responses || response.data.responses.length === 0) {
            return { responses: [], lastEvaluatedKey: null };
        }
        const responses: Response[] = response.data.responses;
        return {
            responses: responses,
            lastEvaluatedKey: response.data.last_evaluated_key || null,
        };
    },

    getUserResponse(token: string, response_id: string) {
        return challengeServiceClient.get('/challenge/user-response', {
            params: {
                response_id,
            },
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getUserPosts(
        user_id: string,
        limit = 10,
        last_evaluated_key: any = null
    ): Promise<{ posts: Post[]; lastEvaluatedKey: any }> {
        const response = await challengeServiceClient.get('/challenge/user-posts', {
            params: {
                user_id,
                limit,
                last_evaluated_key: last_evaluated_key ? JSON.stringify(last_evaluated_key) : null,
            },
        });
        if (!response.data.posts || response.data.posts.length === 0) {
            return { posts: [], lastEvaluatedKey: null };
        }
        const posts: Post[] = response.data.posts;
        return {
            posts: posts,
            lastEvaluatedKey: response.data.last_evaluated_key || null,
        };
    },

    async getUserPost(
        post_id: string
    ): Promise<{ post: Post; user_likes: string[]; liked: boolean }> {
        const postResp = await challengeServiceClient.get('/challenge/user-post', {
            params: { post_id },
        });

        if (!postResp?.data?.post) {
            throw new Error('Post not found');
        }

        return {
            post: postResp.data.post,
            user_likes: postResp.data.user_likes || [],
            liked: !!postResp.data.liked,
        };
    },

    getChallenge(challenge_id: string) {
        return challengeServiceClient.get('/challenge/get-challenge', {
            params: {
                challenge_id,
            },
        });
    },

    async getUserChallengeAuth(challenge_id: string, token: string): Promise<Challenge | null> {
        const resp = await challengeServiceClient.get('/challenge/user-challenge', {
            params: {
                challenge_id,
            },
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        if (resp.data && resp.data.challenge) {
            return resp.data.challenge;
        }
        return null;
    },

    async getUserChallenge(challenge_id: string): Promise<Challenge | null> {
        const resp = await challengeServiceClient.get('/challenge/get-challenge', {
            params: {
                challenge_id,
            },
        });
        if (resp.data && resp.data.challenge) {
            return resp.data.challenge;
        }
        return null;
    },

    getUserResponseAuth(response_id: string, token: string) {
        return challengeServiceClient.get('/challenge/user-response', {
            params: {
                response_id,
            },
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    getUserResponsesCount(user_id: string) {
        // Make the GET request using challengeServiceClient
        return challengeServiceClient.get('/challenge/user-responses-count', {
            params: {
                user_id,
            },
        });
    },

    updateChallenge(accessToken: string, challenge: Challenge) {
        return challengeServiceClient.post('/challenge/update', challenge, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        });
    },

    getPrizePhotoSignedUrl(challenge_id: string, token: string) {
        return challengeServiceClient.get('/challenge/get-signed-url-for-prize-photo', {
            params: {
                challenge_id,
            },
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    completeMultipartUpload(object_name: string, upload_id: string, parts: Array<any>) {
        return challengeServiceClient.post('/challenge/complete-multipart-upload', {
            object_name,
            upload_id,
            parts,
        });
    },

    abortMultipartUpload(object_name: string, upload_id: string) {
        return challengeServiceClient.post('/challenge/abort-multipart-upload', {
            object_name,
            upload_id,
        });
    },
};
